import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@pkg/components';
import { EidCategory, type EidCategoryDataHash, type Tag } from '@pkg/types';

import { LinkItem } from '@app/helpers';

import UndoAction from '../../Elements/UndoAction/UndoAction';
import AddIcon from '../icons/add-icon-purple.svg?react';
import AddIconHover from '../icons/add-icon-white.svg?react';

import { YatButton } from './YatButton/YatButton';
import YatLink from './YatLink/YatLink';
import './_YatLinks.scss';

type Props = {
    editable: boolean;
    currentEmojiTags: Tag[];
    updateTagsQueue: (
        eidCategory: EidCategory,
        data?: string,
        deleteHashes?: string[],
        ignoreDirtyState?: boolean,
    ) => void;
    invalidEmojiId: boolean;
    emojiIdNotFound: boolean;
    isLoading: boolean;
    setIsDirty: (isDirty: boolean) => void;
    emojiId: string;
};

interface InlineTag extends Tag {
    newLink?: boolean;
}

function YatLinks({
    currentEmojiTags,
    editable,
    updateTagsQueue,
    invalidEmojiId,
    emojiIdNotFound,
    isLoading,
    setIsDirty,
    emojiId,
}: Props) {
    const category = EidCategory.DELIMITED_WEB_URL;
    const { t } = useTranslation('yat_page');
    const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);
    const [linkTags, setLinkTags] = useState<InlineTag[]>();
    const [deletedItem, setDeletedItem] = useState<LinkItem>();
    const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);
    const [linkItems, setLinkItems] = useState<LinkItem[]>([]);
    const [currentLastPosition, setCurrentLastPosition] = useState<number>(0);
    const [disableDrag, setDisableDrag] = useState(false);

    const addNewLinkForm = useCallback(
        (ignoreDirtyState = false) => {
            const newLinkItem = new LinkItem();
            newLinkItem.order = currentLastPosition;
            updateTagsQueue(category, newLinkItem.convertToDelimitedLinkTag(), [], ignoreDirtyState);
        },
        [currentLastPosition, category, updateTagsQueue],
    );

    const saveAll = useCallback(() => {
        linkItems.forEach((linkItem) => {
            updateTagsQueue(category, linkItem.convertToDelimitedLinkTag(), [linkItem.hash]);
        });
    }, [linkItems, category, updateTagsQueue]);

    useEffect(() => {
        if (currentEmojiTags) {
            const tags = currentEmojiTags.filter((tag) => tag.tag === category);
            if (tags.length === 0 && editable) {
                addNewLinkForm(!initialLoadComplete);
                setInitialLoadComplete(true);
            } else {
                setLinkTags([...tags]);
                const newLinkItems = tags.map((link) => new LinkItem(link.data, link.hash));
                newLinkItems.sort((a, b) => a.order - b.order);
                const lastOrder = newLinkItems.length ? newLinkItems[newLinkItems.length - 1].order + 1 : 0;
                setCurrentLastPosition(lastOrder);
                setLinkItems(newLinkItems);
            }
        }
    }, [currentEmojiTags, editable, addNewLinkForm, category, initialLoadComplete, setInitialLoadComplete]);

    const onDragEnd = useCallback(
        (result) => {
            // dropped outside the list
            if (!result.destination) {
                return;
            }
            const items = reorder(linkItems, result.source.index, result.destination.index) as LinkItem[];
            setLinkItems(items);
            saveAll();
        },
        [linkItems, saveAll],
    );

    const reorder = (list: LinkItem[], startIndex, endIndex) => {
        const result: LinkItem[] = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        result.forEach((linkItem, i) => {
            linkItem.order = i;
        });
        return result;
    };
    const getItemStyle = (isDragging, draggableStyle) => {
        const dragStyle = isDragging
            ? {
                  height: 'auto',
                  width: '100%',
                  display: 'flex',
                  position: 'relative',
                  borderRadius: '25px',
                  boxShadow: '0 10px 50px 0 rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#ffffff',
                  outline: 'none',
              }
            : {
                  width: '100%',
              };
        return {
            ...dragStyle,
            ...draggableStyle,
        };
    };
    const getListStyle = (isDraggingOver) => ({
        borderColor: isDraggingOver ? 'rgba(0,0,0,0.2)' : '',
    });

    const noLinks = linkTags && linkTags.length === 0;

    const handleUndo = (): void => {
        if (deletedItem) {
            updateTagsQueue(category, deletedItem.convertToDelimitedLinkTag(), []);
        }

        setShowDeleteAlert(false);
    };

    const Links = (provided, snapshot): ReactNode => {
        if (!editable && noLinks) {
            return <NoLinkLivePlaceHolder />;
        }
        return yatLinks(provided, snapshot);
    };

    const placeholderCopy = () => {
        if (emojiIdNotFound) {
            return <h2>{t('yat_page_emoji_not_found')}</h2>;
        }
        if (invalidEmojiId) {
            return <h2>{t('yat_page_invalid_emoji')}</h2>;
        }
        return <h2>{t('yat_page_no_links_copy')}</h2>;
    };
    const NoLinkLivePlaceHolder = () => {
        return (
            <div className="no-links-placeholder">
                <img src="/assets/img/empty@2x.png" alt={t('no_links_alt')} />
                {placeholderCopy()}
            </div>
        );
    };

    const yatLinks = useCallback(
        (_provided, _snapshot) => {
            const handleDelete = (deletedLinkItem: LinkItem): void => {
                setDeletedItem(deletedLinkItem);
                const deleteArray = deletedLinkItem.hash ? ([deletedLinkItem.hash] as EidCategoryDataHash[]) : [];
                updateTagsQueue(category, undefined, deleteArray);
                setShowDeleteAlert(true);
            };
            return linkItems.map((linkItem, i) => {
                return (
                    <Draggable
                        isDragDisabled={!editable || !linkItem.hash || disableDrag}
                        key={`${linkItem.hash}`}
                        draggableId={`${linkItem.hash}`}
                        index={i}
                    >
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                onMouseDown={(e) => e.currentTarget.focus()}
                                className="draggable-link"
                            >
                                <YatLink
                                    emojiId={emojiId}
                                    setIsDirty={setIsDirty}
                                    key={i}
                                    updateTagsQueue={updateTagsQueue}
                                    editable={editable}
                                    linkItem={linkItem}
                                    onDelete={handleDelete}
                                    isDragging={snapshot.isDragging}
                                    isLastLink={i === linkItems.length - 1}
                                    onPickerOpened={(open) => {
                                        setDisableDrag(open);
                                    }}
                                />
                            </div>
                        )}
                    </Draggable>
                );
            });
        },
        [emojiId, linkItems, editable, disableDrag, setIsDirty, category, updateTagsQueue],
    );

    const AddNewLinkButton = useCallback(() => {
        return (
            <div className="button-holder">
                <YatButton
                    onClick={() => addNewLinkForm()}
                    size={'fluid'}
                    icon={<AddIcon />}
                    hoverIcon={<AddIconHover />}
                >
                    {t('add_new_link')}
                </YatButton>
            </div>
        );
    }, [addNewLinkForm, t]);

    return (
        <div
            className={`yat-links ${!editable ? 'live-page' : ''}  ${
                isLoading ? 'loading' : noLinks ? 'no-links' : ''
            } `}
        >
            <Skeleton on={isLoading && !!yatLinks} variant="link">
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={getListStyle(snapshot.isDraggingOver)}
                            >
                                {Links(provided, snapshot)}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                {editable && <AddNewLinkButton />}
            </Skeleton>
            <UndoAction actionText={t('link_removed')} onUndoClick={handleUndo} show={showDeleteAlert} />
        </div>
    );
}

export default YatLinks;
